import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useDeleteKfoReportMutation,
  useGetKfoReportQuery,
  useIssueKfoReportMutation,
  useLazyExportKfoReportXlsxQuery,
  useValidateKfoReportMutation,
  type KfoReport,
  type KfoReportItem,
  type KfoValidation,
  type KfoValidationMessage,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { CreateReportForm } from './CreateReportForm';
import { KfoFieldSuggestionsDialog } from './KfoFieldSuggestionsDialog';
import { useQtySaver } from './useQtySaver';
import {
  Actions,
  Banner,
  Columns,
  DataTable,
  Muted,
  Panel,
  PanelTitle,
  QtyInput,
  REPORT_KIND_LABEL,
  Row,
  SmallInput,
  TableWrap,
  Tab,
  Tabs,
  Tag,
  errorText,
  fmtDate,
  fmtDateTime,
  fmtNum,
  isRfiKind,
  reportTitle,
  safeFileName,
  saveBlob,
  validationFrom,
} from './ui';

const ReportItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const MsgList = styled.ul`
  margin: 6px 0 0;
  padding-left: 18px;
  max-height: 220px;
  overflow: auto;

  li {
    margin-bottom: 2px;
  }
  button {
    border: none;
    background: none;
    padding: 0;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    font: inherit;
    text-decoration: underline;
  }
`;

type ListFilter = 'all' | 'draft' | 'issued';

/** Сколько позиций отчёта рисовать сразу (дальше — «Показать ещё»). */
const ITEMS_CHUNK = 300;

export function KfoReportsTab({
  projectId,
  canWrite,
  reports,
  reportsLoading,
  selectedId: selectedRaw,
  onSelect,
  currentReportId,
  onUseInRegistry,
  onOpenLine,
}: {
  projectId: string;
  canWrite: boolean;
  reports: KfoReport[];
  reportsLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  currentReportId: string | null;
  onUseInRegistry: (id: string | null) => void;
  onOpenLine: (lineId: string) => void;
}) {
  const [filter, setFilter] = useState<ListFilter>('all');
  // Ничего не выбрано — открываем черновик, в который сейчас вводится объём.
  const selectedId =
    selectedRaw ?? (reports.some((r) => r.id === currentReportId) ? currentReportId : null);
  const shown = useMemo(
    () => reports.filter((r) => filter === 'all' || r.status === filter),
    [reports, filter],
  );

  return (
    <Columns>
      <div>
        {canWrite && (
          <Panel>
            <PanelTitle>Новый черновик</PanelTitle>
            <CreateReportForm projectId={projectId} onCreated={(r) => onSelect(r.id)} />
          </Panel>
        )}
        <Panel>
          <PanelTitle>Отчёты и приложения</PanelTitle>
          <Tabs style={{ marginBottom: 12 }}>
            {(
              [
                ['all', 'Все'],
                ['draft', 'Черновики'],
                ['issued', 'Выпущенные'],
              ] as [ListFilter, string][]
            ).map(([k, label]) => (
              <Tab key={k} type="button" $active={filter === k} onClick={() => setFilter(k)}>
                {label}
              </Tab>
            ))}
          </Tabs>
          {reportsLoading && <Muted>Загрузка…</Muted>}
          {!reportsLoading && shown.length === 0 && <Muted>Пока нет.</Muted>}
          {shown.map((r) => (
            <ReportItem
              key={r.id}
              type="button"
              $active={r.id === selectedId}
              onClick={() => onSelect(r.id)}
            >
              <div style={{ fontWeight: 600, fontSize: 13 }}>{reportTitle(r)}</div>
              <Muted style={{ fontSize: 12, marginTop: 2 }}>
                <Tag $tone={r.status === 'issued' ? 'success' : 'neutral'}>
                  {r.status === 'issued' ? 'выпущен' : 'черновик'}
                </Tag>{' '}
                позиций: {r.items_count}
                {r.id === currentReportId ? ' · ввод в реестре' : ''}
              </Muted>
            </ReportItem>
          ))}
        </Panel>
      </div>

      <div>
        {selectedId ? (
          <ReportDetail
            key={selectedId}
            reportId={selectedId}
            canWrite={canWrite}
            isCurrent={selectedId === currentReportId}
            onUseInRegistry={onUseInRegistry}
            onDeleted={() => {
              if (selectedId === currentReportId) onUseInRegistry(null);
              onSelect(null);
            }}
            onOpenLine={onOpenLine}
          />
        ) : (
          <Muted>
            Выберите отчёт слева или создайте черновик. Объём за период удобнее всего вводить прямо
            в реестре: выберите черновик во вкладке «Реестр» и заполняйте колонку «Физический
            объём».
          </Muted>
        )}
      </div>
    </Columns>
  );
}

function ReportDetail({
  reportId,
  canWrite,
  isCurrent,
  onUseInRegistry,
  onDeleted,
  onOpenLine,
}: {
  reportId: string;
  canWrite: boolean;
  isCurrent: boolean;
  onUseInRegistry: (id: string | null) => void;
  onDeleted: () => void;
  onOpenLine: (lineId: string) => void;
}) {
  const { data, isLoading, error } = useGetKfoReportQuery(reportId);
  const [validate, { isLoading: validating }] = useValidateKfoReportMutation();
  const [issue, { isLoading: issuing }] = useIssueKfoReportMutation();
  const [remove, { isLoading: deleting }] = useDeleteKfoReportMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportKfoReportXlsxQuery();

  const [validation, setValidation] = useState<KfoValidation | null>(null);
  const [notice, setNotice] = useState<{
    tone: 'success' | 'danger' | 'info';
    text: string;
  } | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(ITEMS_CHUNK);

  const report = data?.report;
  const isDraft = report?.status === 'draft';
  const editable = canWrite && isDraft;
  const saver = useQtySaver(editable ? reportId : null);

  const items: KfoReportItem[] = useMemo(() => {
    const all = data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((i) =>
      [String(i.ext_key), i.ep_code, i.ep_name, i.element, i.rd_sheet_code]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(needle)),
    );
  }, [data, search]);

  if (isLoading) return <Muted>Загрузка отчёта…</Muted>;
  if (error || !report || !data) {
    return <Banner $tone="danger">{errorText(error, 'Не удалось открыть отчёт.')}</Banner>;
  }

  const rfi = isRfiKind(report.kind);
  const totalQty = data.items.reduce((s, i) => s + (i.qty ?? 0), 0);
  const saving = saver.status === 'saving';

  /**
   * Дождаться сохранения введённого объёма (в том числе уже летящего PUT):
   * иначе проверка, выпуск и XLSX уйдут на сервер раньше последнего ввода и
   * покажут «Проверка пройдена» / форму по старым позициям.
   */
  async function saveFirst(): Promise<boolean> {
    const ok = await saver.flush();
    if (!ok) {
      setNotice({
        tone: 'danger',
        text: 'Не все объёмы сохранены — исправьте отмеченные поля и повторите.',
      });
    }
    return ok;
  }

  async function onValidate() {
    setNotice(null);
    if (!(await saveFirst())) return;
    try {
      const v = await validate(reportId).unwrap();
      setValidation(v);
      if (v.errors.length === 0 && v.warnings.length === 0) {
        setNotice({ tone: 'success', text: 'Проверка пройдена: ошибок и предупреждений нет.' });
      }
    } catch (e) {
      setValidation(null);
      setNotice({ tone: 'danger', text: errorText(e, 'Не удалось проверить отчёт.') });
    }
  }

  async function onIssue() {
    setNotice(null);
    if (!(await saveFirst())) return;
    // Сначала проверка: с ошибками сервер не выпустит, предупреждения — на усмотрение.
    let v: KfoValidation | null = null;
    try {
      v = await validate(reportId).unwrap();
      setValidation(v);
    } catch {
      v = null;
    }
    if (v && v.errors.length > 0) {
      setNotice({
        tone: 'danger',
        text: `Выпуск невозможен: ошибок ${v.errors.length}. Исправьте позиции.`,
      });
      return;
    }
    const warn = v && v.warnings.length > 0 ? `\n\nЕсть предупреждения: ${v.warnings.length}.` : '';
    if (
      !window.confirm(
        `Выпустить «${reportTitle(report!)}»? Значения «Ранее…» зафиксируются, правка станет невозможна.${warn}`,
      )
    ) {
      return;
    }
    try {
      await issue(reportId).unwrap();
      setNotice({ tone: 'success', text: 'Отчёт выпущен. Скачайте печатную форму для Заказчика.' });
      if (isCurrent) onUseInRegistry(null);
    } catch (e) {
      const val = validationFrom(e);
      if (val) {
        setValidation(val);
        setNotice({ tone: 'danger', text: 'Сервер не выпустил отчёт: есть ошибки проверки.' });
      } else {
        setNotice({ tone: 'danger', text: errorText(e, 'Не удалось выпустить отчёт.') });
      }
    }
  }

  async function onDelete() {
    if (!window.confirm(`Удалить черновик «${reportTitle(report!)}»?`)) return;
    try {
      await remove(reportId).unwrap();
      onDeleted();
    } catch (e) {
      setNotice({ tone: 'danger', text: errorText(e, 'Не удалось удалить черновик.') });
    }
  }

  async function onDownload() {
    setNotice(null);
    if (!(await saveFirst())) return;
    try {
      const blob = await exportXlsx(reportId).unwrap();
      const base = rfi ? 'Приложение_к_RFI' : 'Отчет_СМР';
      saveBlob(blob, `${safeFileName(`${base}_${fmtDate(report!.report_date)}`)}.xlsx`);
    } catch (e) {
      setNotice({ tone: 'danger', text: errorText(e, 'Не удалось сформировать XLSX.') });
    }
  }

  const msgList = (title: string, list: KfoValidationMessage[], tone: 'danger' | 'warning') =>
    list.length > 0 ? (
      <Banner $tone={tone}>
        <b>
          {title}: {list.length}
        </b>
        <MsgList>
          {list.map((m, i) => (
            <li key={`${m.line_id ?? ''}-${i}`}>
              {m.ext_key !== null && m.line_id ? (
                <button type="button" onClick={() => onOpenLine(m.line_id!)}>
                  Ключ {m.ext_key}
                </button>
              ) : m.ext_key !== null ? (
                <>Ключ {m.ext_key}</>
              ) : null}
              {m.ext_key !== null ? ' — ' : ''}
              {m.message}
            </li>
          ))}
        </MsgList>
      </Banner>
    ) : null;

  return (
    <div>
      <Row>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>{reportTitle(report)}</h2>
          <Muted style={{ marginTop: 4 }}>
            <Tag $tone={isDraft ? 'neutral' : 'success'}>{isDraft ? 'черновик' : 'выпущен'}</Tag>{' '}
            {REPORT_KIND_LABEL[report.kind]} · позиций: {data.items.length} · Σ объёма:{' '}
            {fmtNum(totalQty)}
            {rfi && isDraft && !report.number ? ' · номер присвоится при выпуске' : ''}
            {report.issued_at ? ` · выпущен ${fmtDateTime(report.issued_at)}` : ''}
          </Muted>
        </div>
      </Row>

      <Actions style={{ marginBottom: 12 }}>
        {editable && !isCurrent && (
          <Button
            $size="sm"
            $variant="secondary"
            type="button"
            onClick={() => onUseInRegistry(reportId)}
          >
            Вводить объём в реестре
          </Button>
        )}
        {editable && isCurrent && <Tag $tone="info">ввод объёма в реестре — этот отчёт</Tag>}
        {editable && (
          <Button
            $size="sm"
            $variant="secondary"
            type="button"
            onClick={() => setSuggestOpen(true)}
          >
            Подставить из Прораба
          </Button>
        )}
        {isDraft && (
          <Button
            $size="sm"
            $variant="secondary"
            type="button"
            disabled={validating || saving}
            onClick={onValidate}
          >
            {validating ? 'Проверяю…' : 'Проверить'}
          </Button>
        )}
        {editable && (
          <Button
            $size="sm"
            type="button"
            disabled={issuing || saving || data.items.length === 0}
            onClick={onIssue}
          >
            {issuing ? 'Выпускаю…' : 'Выпустить'}
          </Button>
        )}
        <Button
          $size="sm"
          $variant="secondary"
          type="button"
          disabled={exporting || saving}
          onClick={onDownload}
        >
          {exporting ? 'Готовлю…' : isDraft ? 'XLSX (черновик)' : 'Скачать XLSX'}
        </Button>
        {editable && (
          <Button $size="sm" $variant="ghost" type="button" disabled={deleting} onClick={onDelete}>
            Удалить черновик
          </Button>
        )}
      </Actions>

      {notice && <Banner $tone={notice.tone}>{notice.text}</Banner>}
      {saver.error && <Banner $tone="danger">{saver.error}</Banner>}
      {validation && msgList('Ошибки', validation.errors, 'danger')}
      {validation && msgList('Предупреждения', validation.warnings, 'warning')}

      <Row>
        <SmallInput
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setLimit(ITEMS_CHUNK);
          }}
          placeholder="Найти: Ключ, шифр ЕР, элемент…"
          style={{ minWidth: 260 }}
        />
        {editable && (
          <Muted>
            {saver.status === 'saving'
              ? 'Сохраняю…'
              : saver.status === 'saved'
                ? 'Сохранено'
                : 'Объём можно поправить здесь; пустое поле — убрать позицию.'}
          </Muted>
        )}
      </Row>

      <TableWrap>
        <DataTable>
          <thead>
            <tr>
              <th>Ключ</th>
              <th>Лист РД / рев.</th>
              <th>Шифр и наименование ЕР</th>
              {rfi && <th>Наименование документа</th>}
              <th>Элемент</th>
              <th>Ед.</th>
              <th className="num">Всего ФО по РД</th>
              {rfi ? (
                <>
                  <th className="num">Факт СМР</th>
                  <th className="num">Ранее принято по RFI</th>
                </>
              ) : (
                <>
                  <th className="num">Ранее принятые объёмы</th>
                  <th>Дата учтённого факта</th>
                </>
              )}
              <th>Источник</th>
              <th className="num stick-r">{rfi ? 'Предъявлено в RFI' : 'Факт СМР'}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center', padding: 24 }}>
                  <Muted>
                    {data.items.length === 0
                      ? 'Позиций нет. Введите объёмы в реестре или подставьте из Прораба.'
                      : 'Ничего не найдено.'}
                  </Muted>
                </td>
              </tr>
            )}
            {items.slice(0, limit).map((it) => (
              <tr key={it.id} data-click="true" onClick={() => onOpenLine(it.line_id)}>
                <td className="nowrap">{it.ext_key}</td>
                <td className="nowrap">
                  {it.rd_sheet_code}
                  {it.rd_revision ? (
                    <Muted style={{ fontSize: 11 }}>рев. {it.rd_revision}</Muted>
                  ) : null}
                </td>
                <td className="wrap" style={{ minWidth: 180, maxWidth: 300 }}>
                  <b>{it.ep_code}</b>
                  {it.ep_name ? <Muted style={{ fontSize: 11 }}>{it.ep_name}</Muted> : null}
                </td>
                {rfi && (
                  <td className="wrap" style={{ minWidth: 140, maxWidth: 240 }}>
                    {it.document_name}
                  </td>
                )}
                <td className="wrap" style={{ minWidth: 120, maxWidth: 220 }}>
                  {it.element}
                </td>
                <td className="nowrap">{it.unit}</td>
                <td className="num">{fmtNum(it.fo_total)}</td>
                {rfi ? (
                  <>
                    <td className="num">{fmtNum(it.fact_cum)}</td>
                    <td className="num">{fmtNum(it.accepted_prev)}</td>
                  </>
                ) : (
                  <>
                    <td className="num">{fmtNum(it.prior_qty ?? it.fact_cum)}</td>
                    <td className="nowrap">{fmtDate(it.prior_date)}</td>
                  </>
                )}
                <td>
                  {it.source === 'field' ? <Tag $tone="info">Прораб</Tag> : <Tag>вручную</Tag>}
                </td>
                <td className="num stick-r" onClick={(e) => editable && e.stopPropagation()}>
                  {editable ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <QtyInput
                        value={saver.value(it.line_id, it.qty)}
                        inputMode="decimal"
                        $invalid={saver.isInvalid(it.line_id) || saver.isFailed(it.line_id)}
                        $pending={saver.isPending(it.line_id)}
                        onChange={(e) => saver.set(it.line_id, e.target.value)}
                        onBlur={() => saver.flush()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saver.flush();
                        }}
                        aria-label={`Объём по строке ${it.ext_key}`}
                      />
                      <Button
                        $size="sm"
                        $variant="ghost"
                        type="button"
                        title="Убрать позицию из отчёта"
                        aria-label="Убрать позицию из отчёта"
                        style={{ height: 26, padding: '0 6px' }}
                        onClick={() => {
                          saver.set(it.line_id, '');
                          saver.flush();
                        }}
                      >
                        ✕
                      </Button>
                    </span>
                  ) : (
                    fmtNum(it.qty)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </TableWrap>
      {items.length > limit && (
        <Row style={{ marginTop: 8 }}>
          <Muted>
            Показано {limit} из {items.length}
          </Muted>
          <Button
            $size="sm"
            $variant="secondary"
            type="button"
            onClick={() => setLimit((n) => n + ITEMS_CHUNK)}
          >
            Показать ещё
          </Button>
        </Row>
      )}

      {suggestOpen && (
        <KfoFieldSuggestionsDialog
          reportId={reportId}
          open
          onClose={() => setSuggestOpen(false)}
          onApplied={(n) => {
            setSuggestOpen(false);
            setNotice({
              tone: 'success',
              text: `Подставлено строк: ${n}. Проверьте объёмы перед выпуском.`,
            });
          }}
        />
      )}
    </div>
  );
}
